/**
 * SkinTypeDefStorage - manages local storage of skintypedef content records.
 *
 * skintypedef content arrives as ContentType='skintypedef' records from the backend.
 * The content_body column contains base64-encoded JSON that matches the C# SkinTypeDefDto.
 *
 * Storage structure:
 *   Key:   skintype_def_{skinType}_{languageTypeId}  e.g. skintype_def_1_1
 *   Value: JSON-serialised SkinTypeDefDto (camelCase)
 *   Index: skintype_def_index                         array of stored composite keys
 */

import type { ContentRecord } from '../stores/contentStore';
import type {
  SkinTypeDefDto as ServiceSkinTypeDefDto,
  MVarDto,
  AdminUVarDto,
  AdminHtmlAnswerDto,
  SkinAssessmentListDto as ServiceSkinAssessmentListDto,
  SkinPreloadOverrideDto as ServiceSkinPreloadOverrideDto,
} from '../types/api/service';

export interface StorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface SkinSettingDto {
  name: string;
  value: string;
}

export interface SkinPhiListDto {
  mVarId: string;
  name: string;
  dName: string;
  pos: string;
  // Added when this stopped being sourced from AccountSettingsDto.SkinDef (a per-sign-in backend
  // fetch) and started being read straight from the bundled skintypedef content instead — see
  // toServiceSkinTypeDef. Needed so AccountSettingsView's PracPhi save round-trips it.
  uVarTypeId: string;
}

export interface SkinTemplateOverrideDto {
  templateId: string;
  style: string;
  text: string;
}

// camelCase mirror of service.ts's SkinPreloadOverrideDto (PascalCase) — this file stores the
// bundle's own camelCase JSON; toServiceSkinTypeDef converts to PascalCase for consumers.
export interface SkinPreloadOverrideDto {
  dName: string;
  fieldCode: string;
  type: string;
  value: string;
  languageTypeId: string;
}

export interface SkinAssessmentListDto {
  assessmentId: string;
  dName: string;
  preloadOverrides: SkinPreloadOverrideDto[];
}

// camelCase mirror of service.ts's AdminUVarDto/AdminHtmlAnswerDto, for the same reason as
// SkinPreloadOverrideDto above.
export interface SkinUVarHtmlAnswerDto {
  aText: string;
  languageTypeId: string;
  keyValue: string;
  dataValue: string;
}

export interface SkinUVarDto {
  uvarTypeId: string;
  dName: string;
  mvarName: string;
  htmlUvarAns: SkinUVarHtmlAnswerDto[];
}

// Bumped whenever unpackFromContentRecord starts keeping a field it previously dropped (skinUVars
// was added, and preloadOverrides went from lossy {name,value,type} to the full shape at the same
// time) — a stored record with no schemaVersion, or an older one, is missing that data and needs
// re-unpacking rather than being trusted as-is. See resolveSkinTypeDef in resolveSkinChannelInfo.ts.
export const SKIN_TYPE_DEF_SCHEMA_VERSION = 2;

export interface SkinTypeDefDto {
  skinType: string;
  name: string;
  styleInfo: string;
  languageTypeId: string;
  channelInfo: SkinSettingDto[];
  skinPhi: SkinPhiListDto[];
  skinUVars: SkinUVarDto[];
  sraTypes: SkinSettingDto[];
  templateOverrides: SkinTemplateOverrideDto[];
  assessmentList: SkinAssessmentListDto[];
  downloadedAt: string;
  schemaVersion: number;
}

/**
 * Converts the locally-stored (camelCase) SkinTypeDefDto into the same PascalCase shape
 * AccountSettingsDto.SkinDef used to carry (types/api/service.ts's SkinTypeDefDto) — so
 * AccountSettingsView/AssessmentOptionsView, which already read that shape, need no changes
 * beyond their data source switching from the backend field to the bundled content record.
 */
export function toServiceSkinTypeDef(dto: SkinTypeDefDto): ServiceSkinTypeDefDto {
  const toSettingArray = (arr: SkinSettingDto[]) => arr.map((item) => ({ Name: item.name, Value: item.value }));

  const skinPhi: MVarDto[] = dto.skinPhi.map((item) => ({
    MVarId: item.mVarId,
    Name: item.name,
    DName: item.dName,
    UVarTypeId: item.uVarTypeId,
    Pos: item.pos,
  }));

  const skinUVars: AdminUVarDto[] = dto.skinUVars.map((item) => ({
    UvarTypeId: item.uvarTypeId,
    DName: item.dName,
    MvarName: item.mvarName,
    HtmlUvarAns: item.htmlUvarAns.map((ans): AdminHtmlAnswerDto => ({
      AText: ans.aText,
      LanguageTypeId: ans.languageTypeId,
      KeyValue: ans.keyValue,
      DataValue: ans.dataValue,
    })),
  }));

  const assessmentList: ServiceSkinAssessmentListDto[] = dto.assessmentList.map((item) => ({
    AssessmentId: item.assessmentId,
    DName: item.dName,
    PreloadOverrides: item.preloadOverrides.map((override): ServiceSkinPreloadOverrideDto => ({
      DName: override.dName,
      FieldCode: override.fieldCode,
      Type: override.type,
      Value: override.value,
      LanguageTypeId: override.languageTypeId,
    })),
  }));

  return {
    SkinType: dto.skinType,
    Name: dto.name,
    StyleInfo: dto.styleInfo,
    LanguageTypeId: dto.languageTypeId,
    ChannelInfo: toSettingArray(dto.channelInfo),
    SkinPhi: skinPhi,
    SkinUVars: skinUVars,
    SraTypes: toSettingArray(dto.sraTypes),
    AssessmentList: assessmentList,
  };
}

const normalizeValue = (value: unknown): string => (typeof value === 'string' ? value : '');

const mapSettingArray = (arr: unknown): SkinSettingDto[] => {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr.map((entry: any) => ({
    name: normalizeValue(entry?.Name ?? entry?.name),
    value: normalizeValue(entry?.Value ?? entry?.value),
  }));
};

const mapPreloadOverrideArray = (arr: unknown): SkinPreloadOverrideDto[] => {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr.map((entry: any) => ({
    dName: normalizeValue(entry?.DName ?? entry?.dName),
    fieldCode: normalizeValue(entry?.FieldCode ?? entry?.fieldCode),
    type: normalizeValue(entry?.Type ?? entry?.type),
    value: normalizeValue(entry?.Value ?? entry?.value),
    languageTypeId: normalizeValue(entry?.LanguageTypeId ?? entry?.languageTypeId),
  }));
};

const mapUVarArray = (arr: unknown): SkinUVarDto[] => {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr.map((entry: any) => ({
    uvarTypeId: normalizeValue(entry?.UvarTypeId ?? entry?.uvarTypeId),
    dName: normalizeValue(entry?.DName ?? entry?.dName),
    mvarName: normalizeValue(entry?.MvarName ?? entry?.mvarName),
    htmlUvarAns: Array.isArray(entry?.HtmlUvarAns ?? entry?.htmlUvarAns)
      ? (entry.HtmlUvarAns ?? entry.htmlUvarAns).map((ans: any) => ({
        aText: normalizeValue(ans?.AText ?? ans?.aText),
        languageTypeId: normalizeValue(ans?.LanguageTypeId ?? ans?.languageTypeId),
        keyValue: normalizeValue(ans?.KeyValue ?? ans?.keyValue),
        dataValue: normalizeValue(ans?.DataValue ?? ans?.dataValue),
      }))
      : [],
  }));
};

export class SkinTypeDefStorage {
  private readonly keyPrefix = 'skintype_def_';
  private readonly indexKey = 'skintype_def_index';

  constructor(private storage: StorageInterface) {}

  private storageKey(skinType: string, languageTypeId: string): string {
    return `${this.keyPrefix}${skinType}_${languageTypeId}`;
  }

  private decodeBase64(base64Value: string): string {
    try {
      const runtimeBuffer = (globalThis as any)?.Buffer;
      if (runtimeBuffer?.from) {
        return runtimeBuffer.from(base64Value, 'base64').toString('utf-8');
      }
      return atob(base64Value);
    } catch (error) {
      throw new Error(`SkinTypeDefStorage: failed to decode base64 - ${error}`);
    }
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private async addToIndex(compositeKey: string): Promise<void> {
    try {
      const ids = await this.getIndex();
      if (!ids.includes(compositeKey)) {
        ids.push(compositeKey);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch {
      // non-fatal index write error
    }
  }

  private async removeFromIndex(compositeKey: string): Promise<void> {
    try {
      const ids = await this.getIndex();
      const next = ids.filter((id) => id !== compositeKey);
      await this.storage.setItem(this.indexKey, JSON.stringify(next));
    } catch {
      // non-fatal index write error
    }
  }

  async saveSkinTypeDef(dto: SkinTypeDefDto): Promise<boolean> {
    try {
      const record: SkinTypeDefDto = {
        ...dto,
        downloadedAt: dto.downloadedAt || new Date().toISOString(),
      };

      const skinType = record.skinType || 'default';
      const languageTypeId = record.languageTypeId || '1';
      const compositeKey = `${skinType}_${languageTypeId}`;

      await this.storage.setItem(this.storageKey(skinType, languageTypeId), JSON.stringify(record));
      await this.addToIndex(compositeKey);
      console.log(`✅ SkinTypeDefStorage: saved skintype ${skinType} lang ${languageTypeId}`);
      return true;
    } catch (error) {
      console.error('❌ SkinTypeDefStorage.saveSkinTypeDef failed:', error);
      return false;
    }
  }

  async getSkinTypeDef(skinType: string, languageTypeId = '1'): Promise<SkinTypeDefDto | null> {
    try {
      const raw = await this.storage.getItem(this.storageKey(skinType, languageTypeId));
      return raw ? (JSON.parse(raw) as SkinTypeDefDto) : null;
    } catch (error) {
      console.error('❌ SkinTypeDefStorage.getSkinTypeDef failed:', error);
      return null;
    }
  }

  async getAllSkinTypeDefs(): Promise<SkinTypeDefDto[]> {
    const keys = await this.getIndex();
    const out: SkinTypeDefDto[] = [];

    for (const key of keys) {
      const [skinType, languageTypeId] = key.split('_');
      if (!skinType || !languageTypeId) {
        continue;
      }
      const dto = await this.getSkinTypeDef(skinType, languageTypeId);
      if (dto) {
        out.push(dto);
      }
    }

    return out;
  }

  async clearAll(): Promise<void> {
    const keys = await this.getIndex();
    for (const key of keys) {
      const [skinType, languageTypeId] = key.split('_');
      if (!skinType || !languageTypeId) {
        continue;
      }

      try {
        await this.storage.removeItem(this.storageKey(skinType, languageTypeId));
      } catch {
        // continue deleting other keys
      }
    }

    try {
      await this.storage.removeItem(this.indexKey);
    } catch {
      // non-fatal index removal error
    }
  }

  async unpackFromContentRecord(record: ContentRecord): Promise<SkinTypeDefDto | null> {
    try {
      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ SkinTypeDefStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyColumnIndex = columns.findIndex((column: string) => column.toLowerCase() === 'content_body');
      if (bodyColumnIndex === -1) {
        console.warn('⚠️ SkinTypeDefStorage: content_body column not found');
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyColumnIndex];
      if (!encodedBody) {
        console.warn('⚠️ SkinTypeDefStorage: content_body is empty');
        return null;
      }

      const decodedJson = this.decodeBase64(encodedBody);
      const parsed = JSON.parse(decodedJson);

      const dto: SkinTypeDefDto = {
        skinType: normalizeValue(parsed?.SkinType ?? parsed?.skinType),
        name: normalizeValue(parsed?.Name ?? parsed?.name),
        styleInfo: normalizeValue(parsed?.StyleInfo ?? parsed?.styleInfo),
        languageTypeId: normalizeValue(parsed?.LanguageTypeId ?? parsed?.languageTypeId) || '1',
        channelInfo: mapSettingArray(parsed?.ChannelInfo ?? parsed?.channelInfo),
        skinPhi: Array.isArray(parsed?.SkinPhi ?? parsed?.skinPhi)
          ? (parsed.SkinPhi ?? parsed.skinPhi).map((entry: any) => ({
            mVarId: normalizeValue(entry?.MVarId ?? entry?.mVarId),
            name: normalizeValue(entry?.Name ?? entry?.name),
            dName: normalizeValue(entry?.DName ?? entry?.dName),
            pos: normalizeValue(entry?.Pos ?? entry?.pos),
            uVarTypeId: normalizeValue(entry?.UVarTypeId ?? entry?.uVarTypeId),
          }))
          : [],
        skinUVars: mapUVarArray(parsed?.SkinUVars ?? parsed?.skinUVars),
        sraTypes: mapSettingArray(parsed?.SraTypes ?? parsed?.sraTypes),
        templateOverrides: Array.isArray(parsed?.TemplateOverrides ?? parsed?.templateOverrides)
          ? (parsed.TemplateOverrides ?? parsed.templateOverrides).map((entry: any) => ({
            templateId: normalizeValue(entry?.TemplateId ?? entry?.templateId),
            style: normalizeValue(entry?.Style ?? entry?.style),
            text: normalizeValue(entry?.Text ?? entry?.text),
          }))
          : [],
        assessmentList: Array.isArray(parsed?.AssessmentList ?? parsed?.assessmentList)
          ? (parsed.AssessmentList ?? parsed.assessmentList).map((entry: any) => ({
            assessmentId: normalizeValue(entry?.AssessmentId ?? entry?.assessmentId),
            dName: normalizeValue(entry?.DName ?? entry?.dName),
            preloadOverrides: mapPreloadOverrideArray(entry?.PreloadOverrides ?? entry?.preloadOverrides),
          }))
          : [],
        downloadedAt: new Date().toISOString(),
        schemaVersion: SKIN_TYPE_DEF_SCHEMA_VERSION,
      };

      if (!dto.skinType) {
        console.warn('⚠️ SkinTypeDefStorage: decoded dto missing SkinType');
        return null;
      }

      const saved = await this.saveSkinTypeDef(dto);
      return saved ? dto : null;
    } catch (error) {
      console.error('❌ SkinTypeDefStorage.unpackFromContentRecord failed:', error);
      return null;
    }
  }
}
